/* PureState reviews-real: <ps-reviews-real> (section VIII). Preview fallback / feed rendering from the Liquid
   <template>s (photo tiles, before/after pairs, comments), the summary tally recomputed in JS (average, count,
   5 → 1 distribution patched into [data-ps-avg] / [data-ps-count] / [data-ps-bar]), read-more on clamped tiles,
   the before/after slider and the AJAX comment form ({% form 'contact' %}: success only on contact_posted=true,
   /challenge or a network failure → native submit). No copy in JS: every string is a data-* attribute or lives in the
   templates. Shared helpers come from assets/ps-reviews.js (window.psReviews); local fallbacks keep this file standalone. */
(function () {
  'use strict';
  var doc = document;
  var H = window.psReviews || {};

  function parseJSON(str, fallback) {
    if (H.parseJSON) return H.parseJSON(str, fallback);
    try { return str ? JSON.parse(str) : fallback; } catch (e) { return fallback; }
  }
  function parseMap(str) {
    if (H.parseMap) return H.parseMap(str);
    var out = {};
    (str || '').split(',').forEach(function (pair) {
      var i = pair.indexOf(':');
      if (i > 0) out[pair.slice(0, i).trim()] = pair.slice(i + 1).trim();
    });
    return out;
  }
  function replaceTokens(text, tokens) {
    if (H.replaceTokens) return H.replaceTokens(text, tokens);
    var out = text == null ? '' : String(text);
    Object.keys(tokens || {}).sort(function (a, b) { return b.length - a.length; }).forEach(function (k) {
      var needle = k.charAt(0) === '[' ? k : '[' + k + ']';
      out = out.split(needle).join(String(tokens[k]));
    });
    return out;
  }
  function formatDate(iso) {
    if (H.formatDate) return H.formatDate(iso);
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? m[3] + '.' + m[2] + '.' + m[1] : (iso || '');
  }
  function fetchJSON(url, ms) {
    if (H.fetchJSON) return H.fetchJSON(url, ms);
    return new Promise(function (resolve, reject) {
      var ctrl = 'AbortController' in window ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); reject(new Error('timeout')); }, ms || 4000);
      fetch(url, { signal: ctrl ? ctrl.signal : undefined, credentials: 'omit' })
        .then(function (res) { if (!res.ok) throw new Error(String(res.status)); return res.json(); })
        .then(function (json) { clearTimeout(timer); resolve(json); }, function (err) { clearTimeout(timer); reject(err); });
    });
  }
  function markVerify(el, text, id, label) {
    if (H.markVerify) return H.markVerify(el, text, id, label);
    if (!el || !text || el.hasAttribute('data-ps-verify')) return;
    el.setAttribute('data-ps-verify', text);
    el.setAttribute('data-ps-verify-id', id || 'unnamed');
    el.setAttribute('data-ps-verify-label', label || '');
    el.setAttribute('title', label || text);
  }
  /* Romanian notation: thousands "." and decimals "," (10.220 · 4,8) — mirrors snippets/ps-number-ro.liquid. */
  function numberRo(value, decimals) {
    var n = Number(value);
    if (!isFinite(n)) n = 0;
    var neg = n < 0;
    n = Math.abs(n);
    var fixed = decimals ? n.toFixed(decimals) : String(Math.round(n));
    var parts = fixed.split('.');
    var int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return (neg ? '-' : '') + int + (parts[1] ? ',' + parts[1] : '');
  }
  function ratingLabel(tpl, rating, max) {
    var r = Math.round(Number(rating) * 10) / 10;
    return (tpl || '').split('[rating]').join(String(r).replace('.', ',')).split('[max]').join(String(max));
  }
  function setText(el, text) { if (el) el.textContent = text; }
  function fillStars(el, rating, max) {
    if (!el) return;
    var tpl = el.getAttribute('data-star-label');
    var label = tpl ? ratingLabel(tpl, rating, max) : (el.getAttribute('aria-label') || '').replace(/\d+(?:[.,]\d+)?/, numberRo(rating, 1));
    if (label) el.setAttribute('aria-label', label);
    el.querySelectorAll('.ps-stars__fg').forEach(function (p, i) {
      var fill = Math.max(0, Math.min(100, Math.round((rating - i) * 100)));
      p.style.clipPath = 'inset(0 ' + (100 - fill) + '% 0 0)';
    });
  }
  /* The same tally as the section's Liquid: rating 1..5 only, count_mode verified_only skips unverified reviews. */
  function tally(reviews, verifiedOnly, extra) {
    var total = 0, sum = 0, counts = [0, 0, 0, 0, 0];
    reviews.forEach(function (r) {
      if (verifiedOnly && r.verified !== true) return;
      var n = Math.round(Number(r.rating));
      if (!(n >= 1 && n <= 5)) return;
      total++; sum += n; counts[5 - n]++;
    });
    if (extra) {
      sum += extra.sum;
      extra.counts.forEach(function (c, i) { counts[i] += c; total += c; });
    }
    var pcts = counts.map(function (c) { return total ? Math.round((c * 100) / total) : 0; });
    return { total: total, sum: sum, counts: counts, pcts: pcts, avg: total ? Math.round((sum / total) * 10) / 10 : 0 };
  }
  function imageUrl(img) {
    return img && typeof img === 'object' && typeof img.url === 'string' ? img.url : '';
  }

  if (customElements.get('ps-reviews-real')) return;

  customElements.define('ps-reviews-real', class extends HTMLElement {
    connectedCallback() {
      var d = this.dataset;
      this.sectionId = d.section || '';
      this.scaleMax = parseInt(d.scaleMax, 10) || 5;
      this.verifiedOnly = d.countMode === 'verified_only';
      this.tokens = parseJSON(d.tokens, {});
      this.labels = parseJSON(d.labels, {});
      this.aliases = parseMap(d.aliases);
      this.perPage = parseInt(d.commentsPerPage, 10) || 5;
      this.hideEmpty = d.hideEmpty === 'true';
      this.section = this.closest('.ps-rr') || this;
      this.summary = this.querySelector('[data-ps-summary]');
      this.empty = this.querySelector('[data-ps-empty]');
      this.note = this.querySelector('[data-ps-fallback-note]');
      this.groups = {};
      var self = this;
      this.querySelectorAll('[data-ps-group]').forEach(function (g) { self.groups[g.getAttribute('data-ps-group')] = g; });
      this.photos = this.querySelector('[data-ps-photos]');
      this.pairs = this.querySelector('[data-ps-pairs]');
      this.comments = this.querySelector('[data-ps-comments]');
      this.form = this.querySelector('form.ps-rr-form');
      var ex = (d.extra || '').split('|').map(function (x) { return parseInt(x, 10) || 0; });
      this.extra = ex.length === 6 && (ex[1] + ex[2] + ex[3] + ex[4] + ex[5]) > 0 ? { sum: ex[0], counts: ex.slice(1) } : null;
      this.bind();
      this.observe();
      this.load();
    }

    /* ---------- wiring ---------- */
    bind() {
      var self = this;
      this.addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-ps-read-more]');
        if (!b || !self.contains(b)) return;
        var q = b.parentElement.querySelector('[data-ps-clamp]');
        var open = b.getAttribute('aria-expanded') === 'true';
        if (q) q.classList.toggle('is-expanded', !open);
        b.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
      this.addEventListener('input', function (e) {
        var r = e.target;
        if (!r || !r.hasAttribute || !r.hasAttribute('data-ps-compare-range')) return;
        var cmp = r.closest('[data-ps-compare]');
        var v = Math.max(0, Math.min(100, parseInt(r.value, 10) || 0));
        if (cmp) cmp.style.setProperty('--ps-pos', v + '%');
        r.setAttribute('aria-valuetext', v + '%');
      });
      if (this.form) {
        this.submitBtn = this.form.querySelector('[data-ps-submit]');
        this.fields = this.form.querySelector('[data-ps-form-fields]');
        this.success = this.form.querySelector('[data-ps-form-success]');
        this.error = this.form.querySelector('[data-ps-form-error]');
        this.onSubmit = function (ev) { self.submit(ev); };
        this.form.addEventListener('submit', this.onSubmit);
      }
    }
    disconnectedCallback() {
      if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit);
      if (this.io) this.io.disconnect();
    }
    /* Clamped tiles can only be measured once the section is rendered (content-visibility: auto skips off-screen
       layout), so the read-more buttons are sized on the first intersection. */
    observe() {
      var self = this;
      if (!('IntersectionObserver' in window)) { this.readMore(); return; }
      this.io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (en) { return en.isIntersecting; })) return;
        self.readMore();
        self.io.disconnect();
        self.io = null;
      }, { rootMargin: '200px 0px' });
      this.io.observe(this.section);
    }
    readMore() {
      this.querySelectorAll('[data-ps-read-more]').forEach(function (b) {
        var q = b.parentElement.querySelector('[data-ps-clamp]');
        if (!q) { b.hidden = true; return; }
        b.hidden = q.classList.contains('is-expanded') ? false : !(q.scrollHeight > q.clientHeight + 1);
      });
    }

    /* ---------- data: feed (any theme) or the seed fallback (the section emits data-ps-fallback on non-live themes,
       and on the live theme only with show_seed_on_live; data-verify-text never on live) ---------- */
    load() {
      var self = this, feed = this.dataset.feed, fallback = this.dataset.psFallback;
      var done = function (data, src) { if (self.render(data, src)) self.readMore(); };
      if (feed) {
        fetchJSON(feed).then(function (data) { done(data, 'feed'); }, function () {
          if (fallback) fetchJSON(fallback).then(function (data) { done(data, 'fallback'); }, function () {});
        });
      } else if (fallback) {
        fetchJSON(fallback).then(function (data) { done(data, 'fallback'); }, function () {});
      }
    }
    render(data, src) {
      if (!data || !Array.isArray(data.reviews)) return false;
      var ds = data.dataset || {};
      var max = parseInt(ds.rating_scale_max, 10) || this.scaleMax;
      var reviews = data.reviews.filter(function (r) { return r && typeof r === 'object'; });
      var verifyText = this.dataset.verifyText || '';
      var unverified = ds.seed === true || ds.verified !== true;
      if (this.dataset.summarySource !== 'app_metafield') this.patchSummary(tally(reviews, this.verifiedOnly, this.extra), max);
      this.renderPhotos(reviews, max, verifyText);
      this.renderPairs(reviews, verifyText);
      this.renderComments(Array.isArray(data.comments) ? data.comments : []);
      if (verifyText && unverified) {
        markVerify(this.section, verifyText, this.dataset.verifyId || 'reviews_real_section', this.dataset.verifyLabel || '');
      }
      if (this.note) this.note.hidden = src !== 'fallback';
      this.dataset.psSource = src;
      return true;
    }
    patchSummary(t, max) {
      var s = this.summary;
      if (s) {
        setText(s.querySelector('[data-ps-avg]'), numberRo(t.avg, 1));
        var suffix = s.querySelector('[data-ps-suffix]');
        if (suffix && this.dataset.suffixFromScale === 'true') setText(suffix, '/ ' + max);
        fillStars(s.querySelector('[data-ps-field="stars"]'), t.avg, max);
        var label = t.total === 1 ? this.dataset.labelOne : this.dataset.labelOther;
        setText(s.querySelector('[data-ps-count]'), numberRo(t.total) + (label ? ' ' + label : ''));
        var percent = this.dataset.distributionValue === 'percent';
        for (var n = 5; n >= 1; n--) {
          var i = 5 - n;
          var fill = s.querySelector('[data-ps-bar="' + n + '"]');
          if (fill) fill.style.setProperty('--ps-w', t.pcts[i] + '%');
          setText(s.querySelector('[data-ps-bar-value="' + n + '"]'), percent ? t.pcts[i] + '%' : numberRo(t.counts[i]));
        }
        s.hidden = t.total === 0;
      }
      if (this.empty) this.empty.hidden = t.total > 0;
      var paragraph = this.groups.paragraph;
      if (paragraph && this.dataset.hideParagraph === 'true') paragraph.hidden = t.total === 0;
    }
    template(kind) {
      var tpl = this.querySelector('template[data-ps-tpl="' + kind + '"]');
      return tpl && tpl.content ? tpl : null;
    }
    clearRendered(list) {
      list.querySelectorAll('[data-ps-js]').forEach(function (el) { el.remove(); });
    }
    categoryLabel(cat) {
      return this.labels[cat] || this.labels[this.aliases[cat]] || '';
    }
    renderPhotos(reviews, max, verifyText) {
      var list = this.photos, tpl = this.template('photo');
      if (!list || !tpl) return;
      var self = this, src = this.dataset.photosSource || 'dataset_and_blocks';
      this.clearRendered(list);
      var items = src === 'blocks_only' ? [] : reviews.filter(function (r) { return !!imageUrl(r.photo); });
      items.forEach(function (r, idx) {
        var frag = tpl.content.cloneNode(true);
        var li = frag.querySelector('[data-ps-photo-item]');
        var art = frag.querySelector('.ps-rr-photo');
        if (!li || !art) return;
        var id = String(r.id || ('r' + (idx + 1)));
        li.setAttribute('data-ps-js', '');
        art.id = 'PsPhotoReview-' + id + '-' + self.sectionId;
        if (verifyText) {
          if (r.verified !== true) markVerify(li, verifyText, 'review_' + id, art.getAttribute('data-ps-verify-label') || '');
          var fig = frag.querySelector('[data-ps-field="photo"]');
          if (fig && r.photo.verified !== true) markVerify(fig, verifyText, 'review_photo', fig.getAttribute('data-ps-verify-label') || '');
        }
        var author = (r.author || '').trim();
        var catLabel = self.categoryLabel(r.category || '');
        frag.querySelectorAll('[data-ps-field]').forEach(function (el) {
          switch (el.getAttribute('data-ps-field')) {
            case 'photo':
              var img = el.querySelector('img');
              if (img) { img.src = imageUrl(r.photo); img.alt = r.photo.alt || ''; img.removeAttribute('srcset'); }
              break;
            case 'stars': fillStars(el, Number(r.rating), max); break;
            case 'badge': if (r.verified_purchase !== true) el.remove(); break;
            case 'text': el.textContent = replaceTokens(r.text, self.tokens); break;
            case 'author': if (author) el.textContent = author; break;
            case 'date': if (r.date) { el.textContent = formatDate(r.date); el.setAttribute('datetime', r.date); } else el.remove(); break;
            case 'category': if (catLabel) el.textContent = catLabel; else el.remove(); break;
          }
        });
        list.appendChild(li);
      });
      this.toggleGroup('photos', list.querySelectorAll('[data-ps-photo-item], .ps-rr-photos__item--app').length);
    }
    renderPairs(reviews, verifyText) {
      var list = this.pairs, tpl = this.template('pair');
      if (!list || !tpl) return;
      var self = this, src = this.dataset.photosSource || 'dataset_and_blocks';
      this.clearRendered(list);
      var items = src === 'blocks_only' ? [] : reviews.filter(function (r) {
        var ba = r.before_after;
        return ba && typeof ba === 'object' && imageUrl(ba.before) && imageUrl(ba.after);
      });
      items.forEach(function (r, idx) {
        var ba = r.before_after;
        var frag = tpl.content.cloneNode(true);
        var li = frag.querySelector('[data-ps-pair-item]');
        var fig = frag.querySelector('.ps-rr-pair');
        if (!li || !fig) return;
        var id = String(r.id || ('r' + (idx + 1)));
        li.setAttribute('data-ps-js', '');
        fig.id = 'PsBeforeAfter-' + id + '-' + self.sectionId;
        if (verifyText && ba.verified !== true) markVerify(li, verifyText, 'review_before_after', fig.getAttribute('data-ps-verify-label') || '');
        var author = (r.author || '').trim();
        frag.querySelectorAll('[data-ps-field]').forEach(function (el) {
          switch (el.getAttribute('data-ps-field')) {
            case 'before': el.src = imageUrl(ba.before); el.alt = ba.before.alt || ''; break;
            case 'after': el.src = imageUrl(ba.after); el.alt = ba.after.alt || ''; break;
            case 'period': if (ba.period_label) el.textContent = replaceTokens(ba.period_label, self.tokens); else el.remove(); break;
            case 'caption': if (ba.caption) el.textContent = replaceTokens(ba.caption, self.tokens); else el.remove(); break;
            case 'author': if (author) el.textContent = author; break;
          }
        });
        list.appendChild(li);
      });
      this.toggleGroup('pairs', list.querySelectorAll('[data-ps-pair-item]').length);
    }
    renderComments(comments) {
      var list = this.comments, tpl = this.template('comment');
      if (!list || !tpl) return;
      var self = this;
      this.clearRendered(list);
      var items = comments.filter(function (c) { return c && typeof c === 'object' && c.text; });
      var newest = this.dataset.commentsOrder !== 'oldest';
      items = items.map(function (c, i) { return { c: c, i: i }; }).sort(function (a, b) {
        var da = String(a.c.date || ''), db = String(b.c.date || '');
        var cmp = da === db ? a.i - b.i : (newest ? db.localeCompare(da) : da.localeCompare(db));
        return cmp;
      }).map(function (x) { return x.c; }).slice(0, this.perPage);
      items.forEach(function (c, idx) {
        var frag = tpl.content.cloneNode(true);
        var li = frag.querySelector('[data-ps-comment-item]');
        if (!li) return;
        li.setAttribute('data-ps-js', '');
        li.id = 'PsComment-' + String(c.id || (idx + 1)) + '-' + self.sectionId;
        var author = (c.author || '').trim();
        var reply = c.reply && typeof c.reply === 'object' && c.reply.text ? c.reply : null;
        frag.querySelectorAll('[data-ps-field]').forEach(function (el) {
          switch (el.getAttribute('data-ps-field')) {
            case 'author': if (author) el.textContent = author; break;
            case 'date': if (c.date) { el.textContent = formatDate(c.date); el.setAttribute('datetime', c.date); } else el.remove(); break;
            case 'text': el.textContent = replaceTokens(c.text, self.tokens); break;
            case 'reply': if (!reply) el.remove(); break;
            case 'reply-author': if (reply && (reply.author || '').trim()) el.textContent = reply.author.trim(); break;
            case 'reply-text': if (reply) el.textContent = replaceTokens(reply.text, self.tokens); break;
          }
        });
        list.appendChild(li);
      });
      list.hidden = list.querySelectorAll('[data-ps-comment-item]').length === 0;
    }
    toggleGroup(name, count) {
      var g = this.groups[name];
      if (!g) return;
      if (count > 0) g.hidden = false;
      else if (this.hideEmpty) g.hidden = true;
    }

    /* ---------- comment form: URL-encoded POST to the form action; success only when the final URL carries
       contact_posted=true; /challenge (captcha) or a network failure → native submit (Shopify's own flow) ---------- */
    submit(ev) {
      if (this.busy) { if (ev) ev.preventDefault(); return; }
      if (typeof window.fetch !== 'function' || typeof URLSearchParams === 'undefined' || typeof FormData === 'undefined') return;
      if (this.form.checkValidity && !this.form.checkValidity()) return;
      if (ev) { ev.preventDefault(); ev.stopImmediatePropagation(); }
      var self = this;
      this.setBusy(true);
      this.hide(this.error);
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 10000) : null;
      var body = new URLSearchParams(new FormData(this.form));
      var action = (this.form.getAttribute('action') || location.pathname).split('#')[0];
      fetch(action, {
        method: 'POST',
        body: body,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        redirect: 'follow',
        signal: ctrl ? ctrl.signal : undefined
      }).then(function (res) {
        if (timer) clearTimeout(timer);
        var url = res.url || '';
        if (url.indexOf('/challenge') !== -1) { self.nativeSubmit(); return; }
        if (url.indexOf('contact_posted=true') !== -1) { self.onSuccess(); return; }
        self.onFail();
      }).catch(function () {
        if (timer) clearTimeout(timer);
        self.nativeSubmit();
      });
    }
    nativeSubmit() {
      this.setBusy(false);
      if (typeof HTMLFormElement !== 'undefined') HTMLFormElement.prototype.submit.call(this.form);
    }
    onSuccess() {
      this.setBusy(false);
      if (this.fields) this.fields.hidden = true;
      this.hide(this.error);
      this.show(this.success);
    }
    onFail() {
      this.setBusy(false);
      this.show(this.error);
    }
    setBusy(on) {
      this.busy = on;
      var b = this.submitBtn;
      if (!b) return;
      if (on) {
        if (!b.dataset.labelIdle) b.dataset.labelIdle = b.textContent;
        if (b.dataset.labelSending) b.textContent = b.dataset.labelSending;
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
      } else {
        if (b.dataset.labelIdle) b.textContent = b.dataset.labelIdle;
        b.disabled = false;
        b.removeAttribute('aria-busy');
      }
    }
    show(el) {
      if (!el) return;
      el.hidden = false;
      try { el.focus({ preventScroll: false }); } catch (e) {}
    }
    hide(el) { if (el) el.hidden = true; }
  });
})();
